/**
 * SignaturePad
 *
 * Modal capture surface that produces a PNG data URL from either a typed
 * name (rendered to canvas in a script-like font) or a hand-drawn stroke.
 *
 * Both tabs ultimately funnel through the same canvas → toDataURL pipeline
 * so the rest of the signing flow (and the sealing pipeline in Phase 6)
 * stays one-shape: every signature is a PNG.
 *
 * Accessibility: keyboard-only signers can finish the flow via the Type
 * tab. The Draw tab announces the "clear" / "save" affordances and supports
 * mouse + touch input. We intentionally don't try to make stroke-drawing
 * keyboard-driven — the typed alternative is sufficient.
 */

import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface SignaturePadProps {
  open: boolean;
  initialMode?: 'type' | 'draw';
  defaultName?: string;
  onCancel: () => void;
  onSave: (dataUrl: string) => void;
}

const CANVAS_WIDTH = 480;
const CANVAS_HEIGHT = 160;

export function SignaturePad({
  open,
  initialMode = 'type',
  defaultName = '',
  onCancel,
  onSave,
}: SignaturePadProps) {
  const [mode, setMode] = useState<'type' | 'draw'>(initialMode);
  const [typedName, setTypedName] = useState(defaultName);
  const drawCanvasRef = useRef<HTMLCanvasElement>(null);
  const [drawHasInk, setDrawHasInk] = useState(false);

  useEffect(() => {
    if (open) {
      setMode(initialMode);
      setTypedName(defaultName);
      setDrawHasInk(false);
    }
  }, [open, initialMode, defaultName]);

  // Wire up the draw canvas only when its tab is visible to avoid binding
  // event listeners on a hidden node and to reset state when re-opening.
  useEffect(() => {
    if (!open || mode !== 'draw') return;
    const canvas = drawCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = CANVAS_WIDTH * dpr;
    canvas.height = CANVAS_HEIGHT * dpr;
    canvas.style.width = `${CANVAS_WIDTH}px`;
    canvas.style.height = `${CANVAS_HEIGHT}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#0f172a';

    let drawing = false;
    let last: { x: number; y: number } | null = null;

    const pointFromEvent = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const onDown = (e: PointerEvent) => {
      e.preventDefault();
      drawing = true;
      last = pointFromEvent(e);
      canvas.setPointerCapture(e.pointerId);
    };
    const onMove = (e: PointerEvent) => {
      if (!drawing || !last) return;
      const next = pointFromEvent(e);
      ctx.beginPath();
      ctx.moveTo(last.x, last.y);
      ctx.lineTo(next.x, next.y);
      ctx.stroke();
      last = next;
      setDrawHasInk(true);
    };
    const onUp = () => {
      drawing = false;
      last = null;
    };

    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointerleave', onUp);
    canvas.addEventListener('pointercancel', onUp);

    return () => {
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointerleave', onUp);
      canvas.removeEventListener('pointercancel', onUp);
    };
  }, [open, mode]);

  if (!open) return null;

  function clearDraw() {
    const canvas = drawCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    setDrawHasInk(false);
  }

  function renderTyped(): string | null {
    const trimmed = typedName.trim();
    if (!trimmed) return null;
    const canvas = document.createElement('canvas');
    const dpr = window.devicePixelRatio || 1;
    canvas.width = CANVAS_WIDTH * dpr;
    canvas.height = CANVAS_HEIGHT * dpr;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    ctx.fillStyle = '#0f172a';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = '48px "Brush Script MT", "Lucida Handwriting", cursive';
    ctx.fillText(trimmed, CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2);
    return canvas.toDataURL('image/png');
  }

  function handleSave() {
    if (mode === 'type') {
      const url = renderTyped();
      if (url) onSave(url);
      return;
    }
    const canvas = drawCanvasRef.current;
    if (!canvas) return;
    if (!drawHasInk) return;
    onSave(canvas.toDataURL('image/png'));
  }

  const canSave = mode === 'type' ? typedName.trim().length > 0 : drawHasInk;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Add your signature"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <div className="bg-background border rounded-lg shadow-lg w-full max-w-lg p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Add your signature</h2>
        </div>

        <div className="flex gap-2 border-b">
          <button
            type="button"
            onClick={() => setMode('type')}
            className={`px-3 py-2 text-sm border-b-2 -mb-px ${
              mode === 'type'
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground'
            }`}
          >
            Type
          </button>
          <button
            type="button"
            onClick={() => setMode('draw')}
            className={`px-3 py-2 text-sm border-b-2 -mb-px ${
              mode === 'draw'
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground'
            }`}
          >
            Draw
          </button>
        </div>

        {mode === 'type' ? (
          <div className="space-y-3">
            <Label htmlFor="signature-pad-typed-name">Type your name</Label>
            <Input
              id="signature-pad-typed-name"
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              autoFocus
              maxLength={150}
            />
            <div
              className="border rounded bg-white text-center py-6"
              style={{
                fontFamily: '"Brush Script MT", "Lucida Handwriting", cursive',
                fontSize: 36,
                color: '#0f172a',
              }}
              aria-live="polite"
            >
              {typedName.trim() || <span className="text-muted-foreground text-sm">Preview</span>}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Draw your signature below. If you'd rather skip drawing, switch back to the Type tab.
            </p>
            <canvas
              ref={drawCanvasRef}
              className="border rounded bg-white touch-none"
              aria-label="Signature drawing surface"
            />
            <div>
              <Button type="button" variant="outline" size="sm" onClick={clearDraw}>
                Clear
              </Button>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canSave}>
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}
